import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { PlayerManager } from '@24i/react-native-player';
import { useEventListener } from '@24i/player-utils-react';

import { IAudioTrack, ISourceWithoutDRMProvider, ITextTrack, SeekType } from '@24i/player-base';
import type { LanguageSettingsContext, onModalHiddenParams, PlayerData, StreamData, StreamType } from 'types';
import { Platform, StatusBar, View } from 'react-native';

import { getErrorMessage } from 'Utils';

import { Alert, ActivityIndicator, useLanguageSettingsContext } from 'LocalGluons';
import { Device } from 'DeviceUtils';
import { logEvent } from 'AppManager';
import PlayerOverlay from './components/PlayerOverlay';
import PlayerModal, { playerModalsEnum } from './components/PlayerModal';
import MiniPlayerProgressBar from './components/MiniPlayerProgressBar';
import styles from './styles';
import { AdSkipPolicy } from '../PlayerContainer/hooks/useAdvertisements';
import { SystemInfoHelper } from '../../../AppManager/helpers/SystemInfoHelper';
import { useChromecastContext } from '../ChromecastContext';
import { MediaPlayerState } from '../ChromecastContext/ChromecastOptions';

interface PlayerUIProps {
    playerInstance: PlayerManager;
    item: PlayerData['item'];
    currentItem: PlayerData['item'];
    itemStreamData: StreamData;
    loading: boolean;
    liveItem: PlayerData['item'];
    onPause: () => void;
    isCasting: boolean;
    // modalType: playerModalsEnum | null;
    // showModal: boolean; // (modalType: playerModalsEnum) => void;
    onModalHidden: (params?: onModalHiddenParams) => void;
    onProgress: (event?: any) => void;
    isVideoBlocked: boolean;
    onHorizontalGesture: () => void;
    refreshDetailItem: () => void;
    onBack: () => void;
    onMiniPlayerPress: () => void;
    isMiniPlayer: boolean;
    modalType: playerModalsEnum | null;
    setModalType: (type: playerModalsEnum | null) => void;
    onVideoQualityChanged: (profile) => void;
    onItemChange: (item: PlayerData['item']) => void;
    currentTime?: number;
    onDurationPress: () => void;
    onCurrentTimePress: () => void;
    onResumePlaying: () => void;
    switchToLive: () => void;
    onNextPress?: () => void;
    onPreviousPress?: () => void;
    archiveAvailable: boolean;
    nextBingeItem: PlayerData['item'] | undefined | null;
    gracePeriodInSeconds: number | null;
    setProgress: any;
    progress: any;
    liveLoading: boolean;
    setLiveLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isModalBinge: boolean;
    isLiveVOD: boolean;
    changeItem: (newItem: PlayerData['item']) => void;
    onClose: () => void;
    setNewCurrentItem: (newCurrentItem: PlayerData['item'], setAlsoAsLiveItem: boolean, rewind: boolean) => void;
    adSkipPolicy: AdSkipPolicy | null;
    mediaHouse: string | null;
    switchToTimeshift: (timeshift?: number, startTimeType?: SeekType) => void;
    source: ISourceWithoutDRMProvider;
    scale: number;
    setScale: () => void;
    lowLatency: boolean;
    switchFromLowLatency: () => void;
    isPaused: boolean;
    setPaused: React.Dispatch<React.SetStateAction<boolean>>,
    getStreamData: (
        startTime?: number,
        customStreamType?: StreamType,
        startPositionType?: SeekType
    ) => void;
}


const PlayerUI = ({
    playerInstance,
    item,
    currentItem,
    itemStreamData,
    loading,
    liveItem,
    onPause: onPauseCallback,
    isCasting,
    onModalHidden,
    onProgress,
    isVideoBlocked,
    onHorizontalGesture,
    refreshDetailItem,
    onBack,
    onMiniPlayerPress,
    isMiniPlayer,
    modalType,
    setModalType,
    onVideoQualityChanged,
    onItemChange,
    currentTime,
    onDurationPress,
    onCurrentTimePress,
    onResumePlaying,
    switchToLive,
    onNextPress,
    onPreviousPress,
    archiveAvailable,
    nextBingeItem,
    gracePeriodInSeconds,
    setProgress,
    progress,
    liveLoading,
    setLiveLoading,
    isModalBinge,
    isLiveVOD,
    changeItem,
    onClose,
    setNewCurrentItem,
    adSkipPolicy,
    mediaHouse,
    switchToTimeshift,
    source,
    scale,
    setScale,
    lowLatency,
    switchFromLowLatency,
    isPaused,
    setPaused,
    getStreamData
}: PlayerUIProps): JSX.Element => {
    const { setShowChromecastBinge, mediaStatus, castSession, client, useContinueOnPhoneChannel } = useChromecastContext();
    const continueWatchingChannel = useContinueOnPhoneChannel?.(getStreamData);
    const [selectedAudioTrack, setSelectedAudioTrack] = useState<IAudioTrack | null | any>(null);
    const [selectedTextTrack, setSelectedTextTrack] = useState<ITextTrack | null | any>(null);
    const [audioTracks, setAudioTracks] = useState<IAudioTrack[] | any[]>([]);
    const [textTracks, setTextTracks] = useState<ITextTrack[] | any[]>([]);
    const [alertMessage, setAlertMessage] = useState('');
    const [modalDisplay, setModalDisplay] = useState<'flex' | 'none'>('flex');
    const { selectedPlayerAudioId, setSelectedPlayerAudioId,
        selectedPlayerTextId, setSelectedPlayerTextId } =
        useLanguageSettingsContext() as LanguageSettingsContext;
    // const { getPreferredLanguageSettings } = useLanguageSettingsContext() as LanguageSettingsContext;
    // const onLoadedmetadata = () => {
    //     setLiveLoading(false);
    //     let to;
    //     if (progress) {
    //         to = setTimeout(() => {
    //             setProgress(false);
    //         }, 3000);
    //     }
    //     return () => clearTimeout(to);
    // };
    // useEventListener('loadedmetadata', onLoadedmetadata, playerInstance);
    const { streamType } = itemStreamData;

    // TO DO: find out the way to save picked track and automatically set the right language
    useEffect(() => {
        if (
            playerInstance
            && playerInstance?.audioTracks?.length
            && playerInstance?.textTracks?.length
        ) {
            const getTextTracks = playerInstance.textTracks;
            const getAudioTracks = playerInstance.audioTracks;
            // let getSelectedAudioTrack: IAudioTrack, getSelectedTextTrack: ITextTrack;
            // const { subtitleTrack: preferredTextTrack, audioTrack: preferredAudioTrack } =
            // getPreferredLanguageSettings({ textTracks: getTextTracks, audioTracks: getAudioTracks }) as { subtitleTrack: ITextTrack, audioTrack: IAudioTrack };
            // getSelectedAudioTrack = preferredAudioTrack
            // getSelectedTextTrack = preferredTextTrack
            let getSelectedAudioTrack = getAudioTracks.find(audioTrack =>
                audioTrack.id === selectedPlayerAudioId) || getAudioTracks[0];
            if (!getSelectedAudioTrack) {
                getSelectedAudioTrack = getAudioTracks[0];
                setSelectedPlayerAudioId(getSelectedAudioTrack.id);
            }
            let getSelectedTextTrack = getTextTracks.find(textTrack =>
                textTrack.id === selectedPlayerTextId) || getTextTracks[0];
            if (!getSelectedTextTrack) {
                getSelectedTextTrack = getTextTracks[0];
                setSelectedPlayerTextId(getSelectedTextTrack.id);
            }

            setTextTracks(getTextTracks);
            setAudioTracks(getAudioTracks);

            if (playerInstance.audioTrack?.id !== getSelectedAudioTrack?.id) {
                setSelectedAudioTrack(getSelectedAudioTrack);
                playerInstance.audioTrack = getSelectedAudioTrack;
            }
            if (playerInstance.textTrack?.id !== getSelectedTextTrack?.id) {
                setSelectedTextTrack(getSelectedTextTrack);
                playerInstance.textTrack = getSelectedTextTrack;
            }
        }
    }, [JSON.stringify(playerInstance?.textTracks), JSON.stringify(playerInstance?.audioTracks),
        selectedPlayerAudioId, selectedPlayerTextId]);

    const onTextTrackChange = useCallback(async () => {
        if (!playerInstance) return;
        const newSelectedTextTrack = playerInstance.selectedTextTrack;
        const newTextTracks = playerInstance.textTracks;

        setSelectedTextTrack(newSelectedTextTrack);
        setTextTracks(newTextTracks);
    }, []);

    const onVideoError = async (error) => {
        if (error.errorString === 'Unable to fetch resource') {
            // setConnected(false);
        } else {
            setAlertMessage(getErrorMessage(error));
        }
        console.log(error);
        const systemInfo = new SystemInfoHelper();
        await systemInfo.load();
        logEvent({
            type: 'error', subtype: 'player', content1: error, ...systemInfo.get()
        });
    };


    const onPlay = () => {
        if (!client) setPaused(false);
    };

    const onPause = () => {
        if (!client) setPaused(true);
    };


    useEffect(() => {
        if (playerInstance) {
            playerInstance?.addEventListener?.('play', onPlay);
            playerInstance?.addEventListener?.('pause', onPause);
            playerInstance?.addEventListener?.('error', onVideoError);

            return () => {
                playerInstance?.removeEventListener?.('play', onPlay);
                playerInstance?.removeEventListener?.('pause', onPause);
                playerInstance?.removeEventListener?.('error', onVideoError);
            };
        }
    }, [onTextTrackChange, playerInstance]);

    useEffect(() => {
        if (playerInstance?.paused || mediaStatus?.playerState === MediaPlayerState.PAUSED) {
            setPaused(true);
        } else {
            setPaused(false);
        }
    }, [playerInstance?.paused, mediaStatus?.playerState]);

    const hideModal = () => {
        setModalType(null);
        setShowChromecastBinge && setShowChromecastBinge(false);
    };

    // Theoretically should never happen
    if (!streamType) return <View />;

    if (isMiniPlayer)
        return (
            <View style={styles.miniPlayerUIContainer}>
                <MiniPlayerProgressBar playerInstance={playerInstance} />
            </View>
        );

    const onModalNavigate = () => {
        // onMiniPlayerPress();
        onClose();
        hideModal();
    };
    return (
        <>
            {isMiniPlayer && !Device.isWeb ? (() => null) : <StatusBar hidden />}
            <PlayerOverlay
                maxTimeshift={itemStreamData.maxTimeshift}
                playerInstance={playerInstance}
                onMiniPlayerPress={onMiniPlayerPress}
                item={item}
                liveItem={liveItem || item}
                streamType={streamType}
                onBack={onBack}
                loading={loading}
                // onDurationPress={onDurationPress}
                // onCurrentTimePress={onCurrentTimePress}
                textTracks={textTracks}
                audioTracks={audioTracks}
                selectedAudioTrack={selectedAudioTrack}
                selectedTextTrack={selectedTextTrack}
                // onLanguageSelectedEvent={onLanguageSelectedEvent}
                onModalOpenRequest={setModalType}
                modalOpened={Boolean(modalType && !isModalBinge)}
                modalType={modalType}
                setModalType={setModalType}
                isCasting={isCasting}
                isPaused={isPaused}
                onResumePlaying={onResumePlaying}
                onDurationPress={onDurationPress}
                onCurrentTimePress={onCurrentTimePress}
                currentTime={currentTime}
                switchToLive={switchToLive}
                onHorizontalGesture={onHorizontalGesture}
                onNextPress={onNextPress}
                onPreviousPress={onPreviousPress}
                archiveAvailable={archiveAvailable}
                setAlertMessage={setAlertMessage}
                gracePeriodInSeconds={gracePeriodInSeconds}
                setProgress={setProgress}
                progress={progress}
                showActivityIndicator={castSession ? false : (
                    // !playerInstance?.hasLoadedMetadata ||
                    playerInstance?.currentTime <= 1 ||
                    liveLoading)
                }
                setModalDisplay={setModalDisplay}
                isModalBinge={isModalBinge}
                setLiveLoading={setLiveLoading}
                adSkipPolicy={adSkipPolicy}
                mediaHouse={mediaHouse}
                switchToTimeshift={switchToTimeshift}
                source={source}
                scale={scale}
                setScale={setScale}
                onPlay={onPlay}
                onPause={onPause}
                alertMessage={alertMessage}
                lowLatency={lowLatency}
            // hide={!playerInstance?.hasLoadedMetadata || playerInstance?.currentTime <= 1 || shouldSuspendRendering}
            />
            <PlayerModal
                type={modalType}
                visible={Boolean(modalType)}
                item={item}
                currentItem={currentItem || item}
                liveItem={currentItem || item}
                onVideoQualityChanged={onVideoQualityChanged}
                onClose={hideModal}
                onBack={onBack}
                refreshDetailItem={refreshDetailItem}
                onNavigate={onModalNavigate}
                onItemChange={onItemChange}
                playerInstance={playerInstance}
                textTracks={textTracks}
                audioTracks={audioTracks}
                selectedAudioTrack={selectedAudioTrack}
                selectedTextTrack={selectedTextTrack}
                setSelectedTextTrack={setSelectedTextTrack}
                setSelectedAudioTrack={setSelectedAudioTrack}
                setAudioTracks={setAudioTracks}
                nextBingeItem={nextBingeItem}
                setModalType={setModalType}
                key=""
                setLiveLoading={setLiveLoading}
                modalDisplay={modalDisplay}
                changeItem={changeItem}
                setNewCurrentItem={setNewCurrentItem}
                adSkipPolicy={adSkipPolicy}
                isLiveVOD={isLiveVOD}
                onNextPress={onNextPress}
                switchFromLowLatency={switchFromLowLatency}
                continueWatchingChannel={continueWatchingChannel}
            />
        </>
    );
};

export default PlayerUI;
